/**
 * @mdxui/neo - Components
 *
 * Marketing and landing page components with mdxui-compliant interfaces.
 * - Content at root level (title, subtitle, etc.)
 * - Flat CTA props (callToAction + callToActionUrl)
 * - Zod schemas for validation
 *
 * @example
 * ```tsx
 * import { Hero, Features, Pricing } from '@mdxui/neo'
 *
 * <Hero
 *   title="Welcome"
 *   subtitle="Build amazing things"
 *   callToAction="Get Started"
 *   callToActionUrl="/signup"
 *   variant="centered"
 * />
 * ```
 */

export type {
  GridCrosshatchCenterProps,
  GridCrosshatchEdgesProps,
  GridCrosshatchTopProps,
  GridDenseCenterProps,
  GridDenseEdgesProps,
  GridDenseTopProps,
  GridDiagonalBottomProps,
  GridDiagonalCenterProps,
  GridDiagonalEdgesProps,
  GridDiagonalFullProps,
  GridDiagonalTopProps,
  GridPerspectiveBottomProps,
  GridPerspectiveTopProps,
  GridSolidBottomProps,
  GridSolidCenterProps,
  GridSolidEdgesProps,
  GridSolidFullProps,
  GridSolidTopProps,
  GridSparseCenterProps,
  GridSparseEdgesProps,
  GridSparseTopProps,
} from './backgrounds'
// Backgrounds - Background patterns for Hero sections and other components
export {
  Beams,
  type BeamsProps,
  DotsBottom,
  DotsCenter,
  DotsEdges,
  DotsFull,
  DotsTop,
  GlowBottom,
  type GlowBottomProps,
  GlowCenter,
  type GlowCenterProps,
  GlowTop,
  type GlowTopProps,
  GradientBlur,
  type GradientBlurProps,
  GridCrosshatchCenter,
  GridCrosshatchEdges,
  GridCrosshatchTop,
  GridDashedBottom,
  GridDashedCenter,
  GridDashedEdges,
  GridDashedFull,
  GridDashedTop,
  GridDenseCenter,
  GridDenseEdges,
  GridDenseTop,
  GridDiagonalBottom,
  GridDiagonalCenter,
  GridDiagonalEdges,
  GridDiagonalFull,
  GridDiagonalTop,
  GridPerspectiveBottom,
  GridPerspectiveTop,
  GridSolidBottom,
  GridSolidCenter,
  GridSolidEdges,
  GridSolidFull,
  GridSolidTop,
  GridSparseCenter,
  GridSparseEdges,
  GridSparseTop,
  NoiseTexture,
  type NoiseTextureProps,
  Particles,
  type ParticlesProps,
  SpotlightTopLeft,
  type SpotlightTopLeftProps,
  SpotlightTopRight,
  type SpotlightTopRightProps,
} from './backgrounds'

// Blog - Blog section components
export {
  BlogAuthor,
  type BlogAuthorProps,
  BlogCard,
  type BlogCardLinkProps,
  type BlogCardProps,
  BlogGrid,
  type BlogGridProps,
  BlogHeader,
  type BlogHeaderProps,
  BlogIcon,
  BlogPage,
  type BlogPageLinkProps,
  type BlogPageProps,
  type BlogPost,
  type BlogPostAuthor,
  type BlogPostFAQItem,
  BlogPostHeader,
  type BlogPostHeaderLinkProps,
  type BlogPostHeaderProps,
  BlogPostPage,
  type BlogPostPageLinkProps,
  type BlogPostPageProps,
  BlogPostSEO,
  type BlogPostSEOProps,
  type BlogSEOConfig,
  CodeBlock,
  type CodeBlockProps,
  DisplayAd,
  type DisplayAdLinkProps,
  type DisplayAdProps,
  formatDate,
  getBlogIcon,
  HashScrollHandler,
  type HashScrollHandlerProps,
  type HowToData,
  type HowToStep,
  MarkdownContent,
  markdownTableComponents,
  MdxPre,
  type MdxPreProps,
  parseDate,
  type ReadMoreLinkProps,
  ReadMoreSection,
  type ReadMoreSectionProps,
  type SEOAuthor,
  type SEOPublisher,
  TableOfContents,
  type TableOfContentsAuthor,
  type TableOfContentsProps,
  TagFilter,
  type TagFilterLinkProps,
  type TagFilterProps,
  type TocComponentProps,
  type ViewMode,
  ViewModeToggle,
  type ViewModeToggleLinkProps,
  type ViewModeToggleProps,
} from './blog'

export type { CodeWindowBaseProps, CodeWindowHeroProps, CodeWindowProps, CodeWindowCopyButtonProps } from './code-window'

// Code Window - Syntax-highlighted code blocks
export { CodeWindow, CodeWindowBase, CodeWindowHero, CodeWindowCopyButton } from './code-window'
export type {
  CtaChecklistProps,
  CtaSplitMediaProps,
  CtaCenteredProps,
  CtaCenteredCheckProps,
  CtaOrbitingInstallProps,
  CtaOrbitingProps,
  CtaVariant,
  OrbitItem,
} from './cta'
// CTA - Call-to-action sections
export {
  CTA,
  CTA_VARIANT_MAP,
  type CTAProps,
  CTASection,
  CtaCentered,
  CtaCenteredCheck,
  CtaChecklist,
  CtaOrbiting,
  CtaOrbitingInstall,
  CtaSplitMedia,
  CtaSplitMediaGradient,
  mapCtaSectionProps,
  mapToCtaSplitMediaProps,
} from './cta'
export type { AdaptedFAQProps, FAQNumberedGridProps, FAQSplitCTAProps } from './faq'
// FAQ - Frequently Asked Questions
export {
  type AdaptedFAQInputProps,
  type BlockFaq1Props,
  type BlockFaqItem,
  FAQ,
  type FAQItem,
  FAQNumberedGrid,
  FAQSplitCTA,
  mapFAQItemToBlock,
  mapFAQProps,
} from './faq'
export type {
  // Shared sub-types
  FeatureItem as FeaturesFeatureItem,
  CodeWindowConfig as FeaturesCodeWindowConfig,
  ButtonConfig as FeaturesButtonConfig,
  LearnMoreConfig as FeaturesLearnMoreConfig,
  ContentBlock as FeaturesContentBlock,
  CodeCard,
  CodeTabItem as FeaturesCodeTabItem,
  // Base + Mixins
  FeaturesBaseProps,
  FeaturesArrayProps,
  FeaturesMediaProps,
  FeaturesButtonProps,
  // Variant interfaces
  FeaturesGridProps,
  FeaturesRightProps,
  FeaturesLeftProps,
  FeaturesShowcaseProps,
  FeaturesStickyProps,
  FeaturesGalleryProps,
  FeaturesCodeCardsProps,
  FeaturesSplitCodeProps,
  FeaturesStatsProps,
  FeaturesCodeCardSingleProps,
  FeaturesCodeTabsProps,
  // Union
  FeaturesVariant,
  FeaturesProps,
} from './features'
export type {
  FeaturesRowProps,
  FeaturesDividersFeature,
  FeaturesDividersProps,
  FeaturesTimelineFeature,
  FeaturesTimelineProps,
  FeaturesShowcaseIconsProps,
  FeaturesSplitProps,
  StatsProps,
  FeaturesStickyHighlightsFeature,
  FeaturesStickyHighlightsHighlight,
  FeaturesStickyHighlightsProps,
  FeaturesAgentCardsAgent,
  FeaturesAgentCardsHighlight,
  FeaturesAgentCardsProps,
  FeaturesAgentFlowProps,
  FeaturesAgentTeamProps,
  FeaturesAgentTeamMember,
  PerformanceProps,
  PerformanceStat,
} from './features'
// Features - Feature sections with multiple variants
export {
  Features,
  FeaturesAgentCards,
  FeaturesAgentFlow,
  FeaturesAgentTeam,
  FeaturesCarousel,
  FeaturesCodeCards,
  FeaturesCodeCardSingle,
  FeaturesCodeTabs,
  FeaturesDividers,
  FeaturesGallery,
  FeaturesGrid,
  FeaturesLeft,
  FeaturesRight,
  FeaturesRow,
  FeaturesShowcase,
  FeaturesShowcaseIcons,
  FeaturesSplit,
  FeaturesSplitCode,
  FeaturesStats,
  FeaturesSticky,
  FeaturesStickyHighlights,
  FeaturesTimeline,
  Performance,
  Stats,
} from './features'
export type { FooterMediumProps, FooterLargeProps, FooterSmallProps } from './footer'
// Footer - Site footer
export { FooterLarge, FooterMedium, FooterSmall } from './footer'
// Get Started Modal - Choice modal for OSS product landing pages
// Hero - Hero sections with multiple variants
export {
  Hero,
  type HeroProps,
  HeroBento,
  HeroCenterAnnouncement,
  HeroCentered,
  HeroCenterFeatures,
  HeroCenterTable,
  HeroMediaGrid,
  HeroSplitCode,
  HeroSplitImage,
  HeroSplitTable,
  type HeroCenterTableProps,
  type HeroSplitCodeProps,
  type HeroSplitImageProps,
  type HeroSplitTableProps,
  type HeroVariant,
  type CodeTheme,
  type CodeWindowConfig,
  type FeatureItem,
  type CardContent,
  type InstallSnippetConfig,
  type HeroBaseProps,
  type HeroMediaProps,
  type HeroCenteredProps,
  type HeroCenterFeaturesProps,
  type HeroMediaGridProps,
  type HeroCenterAnnouncementProps,
  type HeroBentoProps,
} from './hero'
export {
  heroBackground,
  heroBackgroundLayers,
  pageNoise,
  HERO_BACKGROUNDS,
  allLegalHeroBackgrounds,
  type SectionBackground,
  type PageBackground,
  type HeroBackgroundLayer,
  type HeroBackgroundPreset,
  type GridStyle,
  type GlowColor,
  type SpotlightColor,
} from './hero/hero-background'
export type {
  HowItWorksSimpleProps,
  HowItWorksSimpleStep,
  HowItWorksStepCode,
  HowItWorksVerticalProps,
  HowItWorksVerticalStep,
  HowItWorksCardsProps,
  HowItWorksCardsStep,
  HowItWorksLongProps,
  HowItWorksLongStep,
} from './how-it-works'
// How It Works - Step-by-step explanation sections
export {
  HowItWorksSimple,
  HowItWorksVertical,
  HowItWorksCards,
  HowItWorksLong,
} from './how-it-works'
// Connect - app/account connection directory (product flow, prop-driven grid|rows).
// Not a landing section — use LogosSection for a "works with" logo wall.
export { Connect, type ConnectProps, type ConnectItem } from './connect'
// Logos - Logo cloud/carousel
export { type LogoItem, type LogosProps, LogosCarousel, LogosInline, LogosSection } from './logos'
export type { NavbarSimpleProps } from './navbar'
// Navbar - Site navigation header
export { NavbarSimple } from './navbar'
export type { PricingMatrixProps, PricingMatrixFeature, PricingMatrixPlan, PricingPlansProps, PricingPlansPlan, PricingPlansBorderedProps, PricingPlansBorderedPlan, PricingCompactProps, PricingPanelProps, PricingAddOnsProps, PricingAddOn, PricingAgentsProps, PricingAgentsAgent, PricingAgentsAttribute, PricingAdapterProps, PricingVariant } from './pricing'
// Pricing - prop-driven style groups (pick a look, pass tiers as data)
export {
  mapToPricingPlansProps,
  mapToPricingCompactProps,
  PRICING_VARIANT_MAP,
  Pricing,
  PricingAddOns,
  PricingAgents,
  PricingCompact,
  PricingMatrix,
  PricingPanel,
  PricingPlans,
  PricingPlansBordered,
  PricingSimple,
  PricingToggle,
} from './pricing'
// Promotion Badge - Subtle "by .do" badge in bottom corner
export { PromotionBadge } from './promotion-badge'
export type { TeamCardsMember, TeamCardsProps, TeamCompactMember, TeamCompactProps } from './team'
// Team - Team member showcase
export { TeamCards, TeamCompact } from './team'
// Testimonials - Customer testimonials
export {
  type TestimonialItem,
  TestimonialSingle,
  type TestimonialSingleProps,
  TestimonialTwitter,
  type TestimonialTwitterProps,
} from './testimonials'

// Link Context - Framework-agnostic navigation
export { LinkProvider, useLink, type LinkProps } from '../lib/link-context'

// AI Components - Chat interfaces powered by AI SDK Elements
// Package version
export const COMPONENTS_VERSION = '0.1.0'
